import React, { useMemo, useReducer, useState, useCallback, useEffect } from 'react';
import { PatientWithAccessRightDTO, ClinicianDTO } from '@peartherapeutics/weaver-api-client';
import './styles.scss';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { flatStateReducer, nestedStateReducer } from 'components/library/utils';
import {
  KakarikoButtonLarge,
  KakarikoConfirmation,
  KakarikoDates,
  KakarikoForm,
  KakarikoSelect,
  KakarikoText,
  Validations,
} from 'components/library';
import moment from 'moment';
import Program from 'utils/program';
import { PROGRAM_CONFIG } from 'utils/constants';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import { IAddPatient, IPatientFormData } from './types';

export const initialFormData: IPatientFormData = {
  firstName: '',
  lastName: '',
  birthDate: {
    year: '',
    month: '',
    day: '',
  },
  email: '',
  externalRef: '',
  accessRight: {
    enabledGiftCardVendors: [],
    externalRef: '',
    featureGroupId: '',
    payer: '',
    inAppContingencyManagement: false,
    configuration: { reward_policy: { name: 'NonMonetary' } },
    program: '',
    type: 'commercial',
  },
  healthCareProviderId: '',
  locationId: '',
};

const AddPatient = (props: IAddPatient) => {
  const { clinicId, clinicians, vendors, featureGroups, onCreateSuccess } =
    props;
  const [localClinicians, setLocalClinicians] = useState<ClinicianDTO[]>(
    clinicians || [],
  );
  const [errors, setErrors] = useReducer<
    React.Reducer<booleanGenericObject, booleanGenericObject>
  >(flatStateReducer, {});
  const [formData, setFormData] = useReducer<
    React.Reducer<IPatientFormData, IPatientFormData>
  >(nestedStateReducer, { ...initialFormData });
  const [newPatientResponse, setNewPatientResponse] = useState<
    Partial<PatientWithAccessRightDTO>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [clinicianName, setClinicianName] = useState('');
  const [treatmentArmName, setTreatmentArmName] = useState('');

  const isPear002aProgram = Program.get()?.name === 'pear-002a';
  const emptyFieldErrorMessage = (field: string): string =>
    `${field} must not be empty.`;

  const dateString: string = useMemo(() => {
    const { year, month, day } = formData.birthDate;
    return moment(`${year}-${month}-${day}`).toISOString()?.slice(0, 10) ?? '';
  }, [formData.birthDate]);

  const clinicianOptions = useMemo(
    () => localClinicians?.map((clinician) => clinician.id) || [],
    [localClinicians],
  );

  const displayClinicianName = useCallback(
    (option: string) => {
      const clinician = localClinicians.find(
        (clinician) => clinician.id === option,
      );
      const firstName = clinician?.firstName || '';
      const lastName = clinician?.lastName || '';
      setClinicianName(`${firstName} ${lastName}`);
      return `${firstName} ${lastName}`;
    },
    [localClinicians],
  );

  useEffect(() => {
    setLocalClinicians(clinicians || []);
  }, [clinicians]);

  useEffect(() => {
    const fetchClinicians = async () => {
      try {
        if ((!localClinicians || localClinicians.length === 0) && clinicId) {
          const { data } = await WeaverApiClientInstance.getAllActiveCliniciansForLocation(
            clinicId,
          );
          setLocalClinicians(data || []);
        }
      } catch (e) {
        ErrorInterceptor.error(e);
      }
    };
    if (open) fetchClinicians();
  }, [open, clinicId, localClinicians]);

  const displayFeatureGroupName = useCallback(
    (option: string) => {
      const { name } = featureGroups.find(
        (featureGroup) => featureGroup.id === option,
      )!;
      setTreatmentArmName(name);
      return name;
    },
    [featureGroups],
  );

  const clearForm = () => {
    setFormData(initialFormData);
    setOpen(false);
    setIsLoading(false);
  };

  const submitNewPatient = useCallback(async () => {
    if (!formData) return;
    setIsLoading(true);
    const policyName =
      formData?.accessRight?.configuration?.reward_policy?.name;
    const inAppContingencyManagement = Boolean(
      policyName && policyName !== 'NonMonetary',
    );
    const contingencyPolicyToSend =
      policyName && policyName !== 'NonMonetary' ? policyName : undefined;
    try {
      const { data } = await WeaverApiClientInstance.createPatient({
        ...formData,
        birthDate: dateString,
        accessRight: {
          ...formData.accessRight,
          inAppContingencyManagement,
          enabledGiftCardVendors: inAppContingencyManagement
            ? formData.accessRight.enabledGiftCardVendors
            : undefined,
          configuration: formData.accessRight.configuration,
          contingencyManagementPolicyName: contingencyPolicyToSend,
        },
      });
      setNewPatientResponse(data);
      onCreateSuccess();
      clearForm();
    } catch (e) {
      let errors = e;
      if (e.error) {
        errors = e.errors.map((m: any) => ({ message: m.message }));
      }
      if (e.response) {
        errors = { message: e.response.data.message };
      }
      ErrorInterceptor.error({ errors });
      setIsLoading(false);
      throw e;
    }
  }, [dateString, formData, onCreateSuccess]);

  const form = useMemo(
    () => (
      <KakarikoForm data={formData} setData={setFormData} setError={setErrors}>
        <KakarikoText
          errorMessage={emptyFieldErrorMessage('Patient First Name')}
          label="Patient First Name"
          name="firstName"
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText
          errorMessage={emptyFieldErrorMessage('Patient Last Name')}
          label="Patient Last Name"
          name="lastName"
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoDates
          label="Patient Date of Birth"
          name="birthDate"
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText
          label="Email"
          name="email"
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoSelect
          label="Health Care Provider"
          name="healthCareProviderId"
          options={clinicianOptions}
          displayValue={displayClinicianName}
          validation={clinicianOptions.length ? [Validations.isNotEmpty] : undefined}
        />
        <KakarikoSelect
          label="Reward Policy"
          name="accessRight.configuration.reward_policy.name"
          options={["NonMonetary", "CM75", "CM150", "CM500"]}
          validation={[Validations.isNotEmpty]}
        />
        {isPear002aProgram && (
          <KakarikoSelect
            label="Treatment Arm"
            name="accessRight.featureGroupId"
            options={featureGroups?.map((featureGroup) => featureGroup.id)}
            displayValue={displayFeatureGroupName}
            validation={[Validations.isNotEmpty]}
          />
        )}
        <KakarikoText
          label="External Patient ID (Optional)"
          name="externalRef"
        />
        <KakarikoText label="Payer (Optional)" name="payer" />
        <KakarikoText
          label="Rx ID (Optional)"
          name="accessRight.externalRef"
        />
      </KakarikoForm>
    ),
    [
      clinicians,
      displayClinicianName,
      displayFeatureGroupName,
      featureGroups,
      formData,
      isPear002aProgram,
    ],
  );

  const confirmation = useMemo(
    () => (
      <>
        <div styleName="container">
          <div styleName="item1">First Name:</div>
          <div styleName="item2">{formData.firstName}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Last Name:</div>
          <div styleName="item2">{formData.lastName}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Date of Birth:</div>
          <div styleName="item2">{dateString}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Email:</div>
          <div styleName="item2">{formData.email}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Health Care Provider:</div>
          <div styleName="item2">{clinicianName}</div>
        </div>
        {isPear002aProgram && (
          <div styleName="container">
            <div styleName="item1">Treatment Arm:</div>
            <div styleName="item2">{treatmentArmName}</div>
          </div>
        )}
      </>
    ),
    [
      clinicianName,
      dateString,
      formData.email,
      formData.firstName,
      formData.lastName,
      isPear002aProgram,
      treatmentArmName,
    ],
  );

  const successModalDialog = useMemo(
    () => (
      <>
        <div styleName="container">
          <div styleName="item1">First Name:</div>
          <div styleName="item2">{newPatientResponse.firstName}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Last Name:</div>
          <div styleName="item2">{newPatientResponse.lastName}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Email:</div>
          <div styleName="item2">{newPatientResponse.email}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Length of Prescription:</div>
          <div styleName="item2">
            {newPatientResponse.currentAccessRight?.durationInDays}
          </div>
        </div>
      </>
    ),
    [
      newPatientResponse.currentAccessRight?.durationInDays,
      newPatientResponse.email,
      newPatientResponse.firstName,
      newPatientResponse.lastName,
    ],
  );

  const openForm = () => {
    setOpen(true);
    const currentProgram = Program.get()?.name;
    const type =
      currentProgram === 'pear-002a' || currentProgram === 'pear-008'
        ? 'study'
        : 'commercial';
    const program = currentProgram
      ? PROGRAM_CONFIG[currentProgram]?.header || currentProgram
      : '';

    setFormData({
      ...initialFormData,
      locationId: clinicId,
      accessRight: {
        ...initialFormData.accessRight,
        enabledGiftCardVendors: vendors?.map((vendor) => vendor.id),
        program,
        type,
      },
    });

    setNewPatientResponse({});
  };

  return (
    <div styleName="wrapper">
      <KakarikoButtonLarge
        buttonTheme="primary"
        handleClick={openForm}
        disabled={!clinicId}
      >
        ADD PATIENT
      </KakarikoButtonLarge>

      <KakarikoConfirmation
        confirmation={confirmation}
        confirmTitle="Confirm Patient Information"
        dialog={form}
        errors={errors}
        handleCancel={clearForm}
        handleClose={clearForm}
        handleSubmit={submitNewPatient}
        isLoading={isLoading}
        open={open}
        title="Add Patient"
        successModal={successModalDialog}
        successBannerText={`Access Code: ${newPatientResponse.currentAccessRight?.accessCode}`}
        successTitleText="Patient Successfully Added"
      />
    </div>
  );
};

export default AddPatient;
